import { FilterOptionDto, SurveyV2 } from '@act/shared/data-transfer-objects';
import { PaginatedTable, PaginatedTableProps } from '@act/storybook';
import { useEffect, useMemo, useState } from 'react';
import { TextField } from '@mui/material';
import { useAppDispatch } from 'redux/store-hooks';
import { getAllClients, clientAppSelectors } from 'redux/slices/clientSlice';
import { getAllSurveys, surveysSelectors } from 'redux/slices/surveys.slice';
import { IndexChildContainer } from 'react-components/member-index/indexContainer';
import { SurveyTableColumns } from './SurveyTableColumns';
import { AdminToolSkeleton } from '../AdminToolSkeleton';
import { EditAssignmentConfigDrawer } from './AssignmentConfiguration/EditAssignmentConfigDrawer';
import { surveyAdminRequests } from 'redux/requests/survey-admin.requests';
import { useApiCall } from 'redux/utils/api-hook';
import { useAppNavigation } from 'react-components/app/routing/routes';

export const SurveyTable = () => {
  const navigate = useAppNavigation();
  const allSurveys = surveysSelectors.useSelectAllSurveys();
  const allClients = clientAppSelectors.useSelectAllClients();
  const [stageAndSegments, loading, getStagesAndSegments] = useApiCall(
    surveyAdminRequests.getAllStagesAndSegments
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [openAssignmentConfig, setOpenAssignmentConfig] =
    useState<boolean>(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState<number>(null);
  const [stageOptions, setStageOptions] = useState<FilterOptionDto[]>([]);
  const [segmentOptions, setSegmentOptions] = useState<FilterOptionDto[]>([]);
  const clientOptions = allClients?.ids.map(id => ({
    label: allClients.entities[id].name,
    value: id,
  }));

  useEffect(() => {
    void getStagesAndSegments({ params: null });
    if (!allClients) {
      dispatch(getAllClients({}));
    }
  }, []);

  useEffect(() => {
    if (stageAndSegments) {
      setSegmentOptions(
        stageAndSegments?.segments.map(item => ({
          label: item.name,
          value: item.id,
        }))
      );
      setStageOptions(
        stageAndSegments?.stages.map(item => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  }, [loading, stageAndSegments]);

  // search by name filtering
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return allSurveys;
    }
    return allSurveys.filter(survey => {
      const name = survey.name.toLowerCase();
      return name.includes(searchTerm.toLowerCase());
    });
  }, [allSurveys, searchTerm]);

  const USER_ADMIN_TABLE_PROPS: PaginatedTableProps<SurveyV2.SurveyDto> = {
    data: filteredData,
    inMemoryPagination: true,
    columnConfig: SurveyTableColumns(
      setOpenAssignmentConfig,
      setSelectedSurveyId
    ),
  };

  const handleCreateSurvey = () => {
    navigate.toSurveyCreator();
  };

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(getAllSurveys({}));
  }, [dispatch]);

  return (
    <AdminToolSkeleton
      title="Surveys"
      toolDescription="Manage Survey Assignment & Due Date configurations here."
      buttonContent="Survey creator"
      onClick={handleCreateSurvey}
    >
      <>
        <TextField
          label="Search by Name"
          variant="outlined"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          fullWidth
          margin="normal"
          sx={{ backgroundColor: '#ffffff' }}
        />
        <IndexChildContainer>
          <PaginatedTable {...USER_ADMIN_TABLE_PROPS} />
        </IndexChildContainer>
        <EditAssignmentConfigDrawer
          isOpen={openAssignmentConfig}
          setOpen={isOpen => {
            setOpenAssignmentConfig(isOpen);
            setSelectedSurveyId(null);
          }}
          surveyId={selectedSurveyId}
          clientOptions={clientOptions}
          segmentOptions={segmentOptions}
          stageOptions={stageOptions}
        />
      </>
    </AdminToolSkeleton>
  );
};
